import { DateFormatter } from '@repo/toolkit/date'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { toast } from 'sonner'

import { createLanguageTestSubmission } from '../../../../api/language-test/create-language-test-submission'
import { finalizeLanguageTestSubmission } from '../../../../api/language-test/finalize-language-test-submission'
import { getActiveLanguageTestSubmission } from '../../../../api/language-test/get-active-language-test-submission'
import { getEnrollmentLanguageTestActions } from '../../../../api/language-test/get-language-test-actions'
import { getMissingQuestions } from '../../../../api/language-test/get-missing-questions'
import { getRemainingTimeInMs } from '../../../../api/language-test/get-remaing-time'
import { goToNextTestSection } from '../../../../api/language-test/go-to-next-section'
import { goToPreviousTestSection } from '../../../../api/language-test/go-to-previous-section'
import { saveLanguageTestResponse } from '../../../../api/language-test/save-language-test-response'
import { LanguageTestSubmissionStep } from '../../../../lib/types/enums/language-test-submissions/language-test-submission-step'
import { ClientLanguageTestSubmission } from '../../../../lib/types/models/language-test/client-language-test-submission'
import { LanguageTestQuestion } from '../../../../lib/types/models/language-test/language-test-question'
import { WithSectionNavigationContext } from '../../../../lib/types/models/language-test/language-test-submission'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'

type TimerState = {
  remainingTimeMs: number
  formattedTime: string
}

type LanguageTestContextType = {
  step: LanguageTestSubmissionStep
  timer: TimerState
  responses: Map<number, number>
  nextButtonLoading: boolean
  previousButtonLoading: boolean
  isFinalizingSubmission: boolean
  currentActiveSubmission?: WithSectionNavigationContext<ClientLanguageTestSubmission>
  isStartingTest?: boolean
  missingQuestions?: LanguageTestQuestion[]
  saveResponse: (questionId: number, selectedAnswerId: number) => void
  startTest: () => void
  handleNextButtonClick: () => void
  handlePreviousButtonClick: () => void
}

export const LanguageTestContext = createContext<LanguageTestContextType>({
  currentActiveSubmission: {} as WithSectionNavigationContext<ClientLanguageTestSubmission>,
  step: LanguageTestSubmissionStep.CHECKING_ACTIVE_SUBMISSION,
  timer: { remainingTimeMs: 0, formattedTime: '00:00' },
  isStartingTest: false,
  nextButtonLoading: false,
  previousButtonLoading: false,
  isFinalizingSubmission: false,
  responses: new Map(),
  saveResponse: () => {},
  startTest: () => {},
  handleNextButtonClick: () => {},
  handlePreviousButtonClick: () => {}
})

const calculateEndTimer = (remainingTimeMs: number): TimerState => {
  const clampedMs = Math.max(0, remainingTimeMs)
  const flooredToSecondMs = clampedMs - (clampedMs % 1000)
  const isExpired = flooredToSecondMs <= 0
  return {
    remainingTimeMs: flooredToSecondMs,
    formattedTime: isExpired ? '00:00' : DateFormatter.formatMsToTime(flooredToSecondMs)
  }
}

// Full screen browser, better for focusing and avoid browser tabs switching
const goIntoFullScreen = () => {
  const fullscreenElement = document.documentElement
  if (fullscreenElement && fullscreenElement.requestFullscreen)
    fullscreenElement.requestFullscreen()
}

// Exit full screen mode
const exitFullScreen = () => {
  if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen()
}

type LanguageTestProviderProps = {
  enrollmentId: number
}

const LanguageTestProvider = ({
  enrollmentId,
  children
}: PropsWithChildren<LanguageTestProviderProps>) => {
  const endTimeRef = useRef<number | null>(null)
  const timerSubmissionIdRef = useRef<number | null>(null)
  const isFinalizingRef = useRef(false)

  const [submission, setSubmission] =
    useState<WithSectionNavigationContext<ClientLanguageTestSubmission>>()
  const [responses, setResponses] = useState<Map<number, number>>(new Map())
  const [missingQuestions, setMissingQuestions] = useState<LanguageTestQuestion[]>([])
  // Tracks 403 errors from mutations (next/prev/finalize/save) — locks the session
  const [isAccessDenied, setIsAccessDenied] = useState(false)
  // Explicit flag set in finalizeSubmission.onSuccess — does not depend on the API response shape
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [timer, setTimer] = useState<TimerState>({
    remainingTimeMs: 0,
    formattedTime: '00:00'
  })

  // Unified 403 handler for all mutations — sets access denied flag instead of step directly
  const handleForbiddenError = useCallback((error: Error) => {
    if ((error as { status?: number }).status !== 403) return showAxiosError(error)
    setIsAccessDenied(true)
    toast.error('Accès refusé à cette session de test')
    exitFullScreen()
  }, [])

  const {
    refetch: refetchLanguageTestSubmissionActions,
    data: languageTestSubmissionActions,
    isLoading: isLoadingLanguageTestSubmissionActions
  } = useQuery({
    queryKey: ['language-test-submission-actions', enrollmentId],
    queryFn: () => getEnrollmentLanguageTestActions(enrollmentId)
  })

  const { isLoading, data: queriedSubmission } = useQuery({
    queryKey: [
      'language-test-submission',
      enrollmentId,
      languageTestSubmissionActions?.hasAnActiveSubmission
    ],
    queryFn: () => getActiveLanguageTestSubmission(enrollmentId),
    // Stop fetching once the submission is submitted locally — prevents a spurious 403
    // after finalization while hasAnActiveSubmission hasn't been refreshed yet
    enabled:
      languageTestSubmissionActions?.hasAnActiveSubmission === true &&
      !submission?.submittedAt &&
      !isSubmitted,
    retry: false
  })

  const {
    data: remainingTimeInMs,
    isLoading: isLoadingRemainingTime,
    isFetching: isFetchingRemainingTime,
    isFetched: isFetchedRemainingTime
  } = useQuery({
    queryKey: ['language-test-remaining-time', enrollmentId, submission?.id],
    queryFn: () => {
      const submissionId = submission?.id
      if (!submissionId) return 0
      return getRemainingTimeInMs(submissionId)
    },
    // Only fetch when there is an ongoing (not yet submitted) submission
    enabled: !!submission?.id && !submission?.submittedAt && !isSubmitted
  })

  // Derive the current step from data/loading/error states — no useState + useEffect needed
  const step = useMemo<LanguageTestSubmissionStep>(() => {
    // 403 from a mutation during an active test (save/navigate/finalize) → session forcibly locked
    if (isAccessDenied) {
      return LanguageTestSubmissionStep.LOCKED
    }

    // Submission completed this session OR was already submitted on a previous visit
    if (isSubmitted || submission?.submittedAt) {
      return LanguageTestSubmissionStep.TEST_SUBMITTED
    }

    // Wait until all initial data (actions, submission, timer) has loaded
    const isTimerLoading =
      isLoadingRemainingTime ||
      (!!submission && isFetchingRemainingTime && timer.remainingTimeMs <= 0) ||
      (!!submission && !isFetchedRemainingTime) ||
      (!!submission && remainingTimeInMs === undefined)

    if (isLoadingLanguageTestSubmissionActions || isLoading || isTimerLoading) {
      return LanguageTestSubmissionStep.CHECKING_ACTIVE_SUBMISSION
    }

    // Active submission exists and is in progress
    if (submission) {
      return LanguageTestSubmissionStep.DISPLAY_TEST_SECTION
    }

    // No active submission — derive from server-side actions
    const { hasSubmittedBefore, isLocked } = languageTestSubmissionActions ?? {}
    if (hasSubmittedBefore) return LanguageTestSubmissionStep.TEST_SUBMITTED
    if (isLocked) return LanguageTestSubmissionStep.LOCKED
    return LanguageTestSubmissionStep.DISPLAY_INSTRUCTIONS
  }, [
    isAccessDenied,
    isSubmitted,
    isLoadingLanguageTestSubmissionActions,
    isLoading,
    isLoadingRemainingTime,
    isFetchingRemainingTime,
    isFetchedRemainingTime,
    remainingTimeInMs,
    timer.remainingTimeMs,
    submission,
    languageTestSubmissionActions
  ])

  const { mutate: createNewSubmission, isPending: isCreatingSubmission } = useMutation({
    mutationFn: () => createLanguageTestSubmission(enrollmentId),
    onSuccess: (data) => {
      goIntoFullScreen()
      setSubmission(data)
    },
    onError: showAxiosError
  })

  const { mutate: finalizeSubmission, isPending: isFinalizingSubmission } = useMutation({
    mutationFn: finalizeLanguageTestSubmission,
    onSuccess: async (data) => {
      // Mark as submitted FIRST — this is the source of truth for the step transition.
      // It must not depend on the API response shape (data.submittedAt might be null
      // if the finalize endpoint returns the full LanguageTestSubmission without that field).
      setIsSubmitted(true)
      setSubmission(data)
      exitFullScreen()
      toast.success(
        data.hasAutoSubmitted
          ? 'Temps écoulé, votre test a été soumis automatiquement'
          : 'Votre test a été soumis avec succès'
      )
      // Only refresh actions — active submission query is already stopped by submission.submittedAt
      await refetchLanguageTestSubmissionActions()
    },
    onError: handleForbiddenError
  })

  const { mutate: navigateToNextSection, isPending: isNavigatingToNextSection } = useMutation({
    mutationFn: goToNextTestSection,
    onSuccess: setSubmission,
    onError: handleForbiddenError
  })

  const { mutate: navigateToPreviousSection, isPending: isNavigatingToPreviousSection } =
    useMutation({
      mutationFn: goToPreviousTestSection,
      onSuccess: setSubmission,
      onError: handleForbiddenError
    })

  const { mutate: fetchMissingQuestions, isPending: isLoadingMissingQuestions } = useMutation({
    mutationFn: getMissingQuestions,
    onSuccess: (data, submissionId) => {
      if (!data?.length) {
        if (!isFinalizingRef.current) finalizeSubmission({ id: submissionId })
        return
      }
      setMissingQuestions(data)
    }
  })

  const saveResponseMutation = useMutation({
    mutationFn: saveLanguageTestResponse,
    onError: handleForbiddenError
  })

  const saveResponse = useCallback(
    (questionId: number, selectedAnswerId: number) => {
      if (!submission) return
      setResponses((prev) => new Map(prev.set(questionId, selectedAnswerId)))
      saveResponseMutation.mutate({ id: submission.id, questionId, selectedAnswerId })
    },
    [submission]
  )

  const handleNextButtonClick = useCallback(() => {
    const submissionId = submission?.id
    if (!submissionId) return
    if (submission.canGoNext) navigateToNextSection(submissionId)
    else if (missingQuestions.length > 0) finalizeSubmission({ id: submissionId })
    else fetchMissingQuestions(submissionId)
  }, [
    submission,
    missingQuestions,
    fetchMissingQuestions,
    finalizeSubmission,
    navigateToNextSection
  ])

  const handlePreviousButtonClick = useCallback(() => {
    const submissionId = submission?.id
    if (!submissionId) return
    if (missingQuestions.length > 0) {
      setMissingQuestions([])
      return
    }
    navigateToPreviousSection(submissionId)
  }, [submission, missingQuestions, navigateToPreviousSection])

  // Keep isFinalizingRef in sync so the timer callback can check it without a stale closure
  useEffect(() => {
    isFinalizingRef.current = isFinalizingSubmission
  }, [isFinalizingSubmission])

  // Countdown timer — auto-submits when time runs out
  useEffect(() => {
    const submissionId = submission?.id
    const isOngoing = !!submissionId && !submission?.submittedAt && !isSubmitted
    if (!isOngoing) {
      endTimeRef.current = null
      timerSubmissionIdRef.current = null
      setTimer({ remainingTimeMs: 0, formattedTime: '00:00' })
      return
    }

    if (remainingTimeInMs === undefined || isFetchingRemainingTime) {
      return
    }

    if (endTimeRef.current === null || timerSubmissionIdRef.current !== submissionId) {
      const safeRemainingTimeInMs = Math.max(0, remainingTimeInMs)
      endTimeRef.current = Date.now() + safeRemainingTimeInMs
      timerSubmissionIdRef.current = submissionId
      setTimer(calculateEndTimer(safeRemainingTimeInMs))
    }

    const updateTimer = () => {
      if (endTimeRef.current === null) return
      const msLeft = Math.max(0, endTimeRef.current - Date.now())
      const newTimer = calculateEndTimer(msLeft)
      setTimer(newTimer)
      if (newTimer.remainingTimeMs <= 0) {
        endTimeRef.current = null
        timerSubmissionIdRef.current = null
        if (!isFinalizingRef.current) {
          finalizeSubmission({ id: submissionId, hasAutoSubmitted: true })
        }
      }
    }
    updateTimer()
    const intervalId = setInterval(updateTimer, 1000)
    return () => clearInterval(intervalId)
  }, [
    submission?.id,
    submission?.submittedAt,
    isSubmitted,
    remainingTimeInMs,
    isFetchingRemainingTime,
    finalizeSubmission
  ])

  // Sync query data into local submission state.
  // Skip the update if we already have a value from a mutation (more up-to-date),
  // unless the query signals the submission has been submitted.
  useEffect(() => {
    if (!queriedSubmission) return
    setSubmission((prev) => {
      if (!prev || queriedSubmission.submittedAt) return queriedSubmission
      return prev
    })
  }, [queriedSubmission])

  // Rebuild the response map whenever the active submission changes
  useEffect(() => {
    const responseMap = new Map<number, number>()
    submission?.responses?.forEach((response) => {
      responseMap.set(response.questionId, response.selectedAnswerId)
    })
    setResponses(responseMap)
  }, [submission])

  return (
    <LanguageTestContext.Provider
      value={{
        currentActiveSubmission: submission,
        step,
        timer,
        isStartingTest: isCreatingSubmission,
        nextButtonLoading:
          isNavigatingToNextSection || isFinalizingSubmission || isLoadingMissingQuestions,
        previousButtonLoading: isNavigatingToPreviousSection,
        isFinalizingSubmission,
        responses,
        missingQuestions,
        startTest: createNewSubmission,
        saveResponse,
        handleNextButtonClick,
        handlePreviousButtonClick
      }}
    >
      {children}
    </LanguageTestContext.Provider>
  )
}

export default LanguageTestProvider

export const useLanguageTestContext = () => {
  const context = useContext(LanguageTestContext)
  if (!context) {
    throw new Error('useLanguageTestContext must be used within a LanguageTestProvider')
  }
  return context
}
